import { Link } from "wouter";
import SectionLink from "@/components/powlo/SectionLink";
import BillUploadDropzone from "@/components/quote/BillUploadDropzone";
import ContractSigningModal from "@/components/quote/ContractSigningModal";
import CustomerAvailabilityModal from "@/components/availability/CustomerAvailabilityModal";
import PostcodeAddressLookup from "@/components/quote/PostcodeAddressLookup";
import { ProposalHtmlPreview } from "@/components/quote/ProposalHtmlPreview";
import QuoteAccountPanel from "@/components/quote/QuoteAccountPanel";
import ConfidenceStages from "@/components/plan/ConfidenceStages";
import PlanBreakdown from "@/components/plan/PlanBreakdown";
import PlanFinance from "@/components/plan/PlanFinance";
import SystemPlanCard from "@/components/plan/SystemPlanCard";
import PowloLayout from "@/components/powlo/PowloLayout";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_BASE } from "@/constants/api";
import { useQuoteFlow } from "@/contexts/QuoteFlowContext";
import { useCustomerPlan } from "@/hooks/useCustomerPlan";
import { formatAzimuth, formatRoofArea, formatShading } from "@/utils/planViewModel";

export default function Plan() {
  const plan = useCustomerPlan();
  const { hasPendingBillFile } = useQuoteFlow();
  const option = plan.selectedOption;
  const billAlreadyProvided = Boolean(
    plan.form.billFile || plan.form.billData || plan.extractPending || hasPendingBillFile(),
  );

  return (
    <PowloLayout>
      <Seo
        title="Your Energy Plan"
        description="A personalised solar and battery plan from your address, roof model and energy use."
        path="/plan"
      />
      {plan.phase === "checking" && (
        <p className="px-5 py-20 text-center text-powlo-muted">Loading your plan…</p>
      )}
      {plan.phase === "example" && <ExampleState />}
      {plan.phase !== "checking" && plan.phase !== "example" && (
        <>
          <section className="flex flex-col gap-4 px-5 pb-8 pt-14 sm:px-8 lg:px-20">
            <div className="text-sm font-bold tracking-widest text-powlo-indigo">THE ENERGY PLAN</div>
            <h1 className="m-0 max-w-3xl font-powlo-display text-4xl font-bold leading-tight tracking-tight sm:text-6xl">
              {plan.greetingName ? `Hello ${plan.greetingName}. Here's your plan.` : "Here's your plan."}
            </h1>
            <p className="m-0 max-w-2xl text-lg leading-relaxed text-powlo-muted">
              {plan.submittedLead
                ? `Plan ref ${plan.submittedLead.reference}. Review the proposal, then agree a date, sign, and choose how to pay.`
                : "This uses your address, roof model and usage. Fix the price when you are happy with the system."}
            </p>
            {plan.addressLine && (
              <div className="text-sm text-powlo-muted-light">{plan.addressLine}</div>
            )}
          </section>

          <section className="flex flex-col gap-6 px-5 pb-16 sm:px-8 lg:px-20">
            {!plan.form.address.trim() && (
              <div className="max-w-xl rounded-3xl border border-powlo-border bg-white p-5">
                <h2 className="m-0 mb-3 font-powlo-display text-2xl font-bold">Confirm the property</h2>
                <PostcodeAddressLookup
                  tone="light"
                  submitLabel="Use this address"
                  onAddressSelected={(fields) => {
                    plan.updateAddressFields(fields);
                    void plan.confirmAddressEnrichment(fields);
                  }}
                />
              </div>
            )}
            {plan.recommendError && (
              <div className="flex items-center gap-3">
                <p className="m-0 text-sm text-red-700">{plan.recommendError}</p>
                <button type="button" className="text-sm font-bold text-powlo-indigo" onClick={plan.retryRecommend}>
                  Try again
                </button>
              </div>
            )}
            <SystemPlanCard
              option={option}
              greetingName={plan.greetingName}
              addressLine={plan.addressLine}
              customised={plan.customised}
              panelCounts={plan.panelCounts}
              batteryChoices={plan.batteryChoices}
              onPanels={plan.setPanels}
              onBattery={plan.setBattery}
              onReset={plan.resetToRecommended}
              includesEv={plan.includesEv}
              onEv={plan.setIncludesEv}
              heatPump={plan.heatPump}
              onHeatPump={plan.setHeatPump}
              score={plan.helioScore}
              scoreLoading={plan.helioScoreLoading}
              epcRating={plan.epcRating}
              upliftGbp={plan.upliftGbp}
              solarBuilding={plan.form.solarBuildingInsights}
              satelliteImageUrl={plan.form.satelliteImageUrl}
              mapCoordinates={plan.form.mapCoordinates}
              satelliteZoom={plan.form.satelliteZoom}
              satelliteImageSize={plan.form.satelliteImageSize}
              mesh3dUrl={plan.form.mesh3dUrl}
              meshGroundFrame={plan.form.meshGroundFrame}
              onLayout={plan.setRoofLayout}
              onFittedCount={plan.setRoofCapacity}
              onSatellitePlacement={plan.rememberSatellitePlacement}
              onClearCache={() => void plan.clearMapCache()}
              clearingCache={plan.clearingMapCache}
              roofMediaPending={plan.roofMediaPending}
              layoutRevision={plan.layoutRevision}
              loading={!option && !plan.recommendError}
              loadingLabel={plan.extractPending ? "Reading your bill…" : "Building your system…"}
            />
          </section>

          <section className="px-5 pb-14 sm:px-8 lg:px-20">
            <ConfidenceStages current={plan.confidence} />
          </section>

          {!plan.submittedLead && !billAlreadyProvided && (
            <section className="flex flex-col gap-8 px-5 pb-16 sm:px-8 lg:px-20">
              <div className="flex flex-col gap-3">
                <div className="text-sm font-bold tracking-widest text-powlo-indigo">WANT IT SHARPER?</div>
                <h2 className="m-0 font-powlo-display text-3xl font-bold tracking-tight sm:text-5xl">
                  Upload a bill for an exact design
                </h2>
                <p className="m-0 max-w-2xl text-lg leading-relaxed text-powlo-muted">
                  We read usage and tariff from the bill, then rebuild this plan. If the bill cannot be read, enter annual usage yourself.
                </p>
              </div>
              <BillUploadDropzone
                tone="light"
                selectedFile={plan.form.billFile}
                onFileSelected={(file) => void plan.runBillUpload(file)}
              />
              <label className="flex max-w-xs flex-col gap-2 text-sm font-bold">
                Annual usage (kWh)
                <input
                  type="number"
                  min={500}
                  max={50000}
                  value={plan.form.annualKwh}
                  onChange={(event) => plan.setAnnualKwh(Number(event.target.value))}
                  className="h-12 rounded-xl border border-powlo-border bg-white px-3 font-normal"
                />
              </label>
            </section>
          )}

          {option && (
            <section className="flex flex-col gap-6 px-5 pb-16 sm:px-8 lg:px-20">
              <div className="flex flex-col gap-3">
                <div className="text-sm font-bold tracking-widest text-powlo-indigo">THE FULL BREAKDOWN</div>
                <h2 className="m-0 font-powlo-display text-3xl font-bold tracking-tight sm:text-5xl">
                  System, benefits and the reasoning behind them
                </h2>
              </div>
              <PlanBreakdown
                tariffNote={plan.tariffNote}
                kit={option.systemSummaryItems ?? null}
                roofFacing={formatAzimuth(plan.form.solarInsights)}
                roofArea={formatRoofArea(plan.form.solarData?.roofAreaM2 ?? plan.form.solarInsights?.roofAreaM2)}
                shading={formatShading(plan.form.solarInsights?.shadingFactor)}
                irradiance={
                  plan.form.solarData?.irradianceKwhM2
                    ? `${Math.round(plan.form.solarData.irradianceKwhM2)} kWh/m²`
                    : null
                }
                epcRating={plan.epcRating}
                upliftGbp={plan.upliftGbp}
                co2Tonnes={option.co2Tonnes}
                independencePct={option.independencePct}
                annualKwh={plan.form.annualKwh}
                supplier={plan.form.energySupplier || null}
              />
            </section>
          )}

          {option && (
            <section id="pay" className="flex flex-col gap-6 px-5 pb-16 sm:px-8 lg:px-20">
              <div className="flex flex-col gap-3">
                <h2 className="m-0 font-powlo-display text-3xl font-bold tracking-tight sm:text-5xl">How you'd pay</h2>
                <p className="m-0 max-w-2xl text-lg leading-relaxed text-powlo-muted">
                  Pay outright, or spread the cost. The monthly figure matches the quote calculator.
                </p>
              </div>
              <PlanFinance
                price={option.price}
                paymentType={plan.form.paymentType}
                onPaymentType={plan.setPaymentType}
                termMonths={plan.financeTermMonths}
                onTermMonths={plan.setFinanceTermMonths}
                annualBenefit={option.annualBenefitY1 ?? option.annualSavings}
                disclosure={plan.financeDisclosure}
              />
            </section>
          )}

          <section className="dotted-indigo mx-4 mb-14 rounded-[40px] px-6 py-16 text-white sm:px-10 lg:mx-10 lg:px-20">
            {plan.submittedLead ? (
              <SubmittedPlan plan={plan} />
            ) : (
              <AccountClose plan={plan} disabled={!option} />
            )}
          </section>
        </>
      )}
    </PowloLayout>
  );
}

function ExampleState() {
  return (
    <article className="mx-auto flex max-w-3xl flex-col gap-6 px-5 py-16 sm:px-8">
      <p className="text-sm font-bold tracking-widest text-powlo-indigo">ILLUSTRATIVE EXAMPLE</p>
      <h1 className="m-0 font-powlo-display text-4xl font-bold tracking-tight sm:text-6xl">An example plan</h1>
      <p className="rounded-2xl border border-powlo-border bg-white p-4 text-sm text-powlo-muted">
        These figures are a layout sample. They are not a quote for a property. Your plan uses address data, roof modelling, your bill when you upload one, and the live savings calculation.
      </p>
      <SectionLink href="/#check" className="w-fit rounded-full bg-powlo-lime px-6 py-3 font-bold text-powlo-ink no-underline">
        Design my system
      </SectionLink>
    </article>
  );
}

function AccountClose({
  plan,
  disabled,
}: {
  plan: ReturnType<typeof useCustomerPlan>;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h2 className="m-0 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">Happy with this? Let's fix the price.</h2>
        <p className="m-0 max-w-2xl text-lg text-[#E6E5FF]">
          Create your account and we'll save this plan as your proposal. A survey then confirms the technical detail.
        </p>
      </div>
      <div className="grid max-w-xl gap-4">
        <Field label="Name" value={plan.form.customerName} onChange={(value) => plan.update({ customerName: value })} />
        <Field label="Email" type="email" value={plan.form.customerEmail} onChange={(value) => plan.update({ customerEmail: value })} />
        <Field label="Phone" value={plan.form.customerPhone} onChange={(value) => plan.update({ customerPhone: value })} />
      </div>
      <div className="max-w-xl rounded-2xl bg-white p-4 text-powlo-ink">
        <QuoteAccountPanel
          tone="light"
          isAuthenticated={plan.isAuthenticated}
          sessionEmail={plan.userEmail}
          mode={plan.accountMode}
          onModeChange={plan.setAccountMode}
          password={plan.password}
          onPasswordChange={plan.setPassword}
          passwordConfirmation={plan.passwordConfirmation}
          onPasswordConfirmationChange={plan.setPasswordConfirmation}
          error={plan.submitError}
        />
      </div>
      <button
        type="button"
        disabled={disabled || plan.isSubmitting || !plan.form.customerName.trim() || !plan.form.customerEmail.trim()}
        onClick={() => void plan.handleSubmit()}
        className="flex h-16 w-fit items-center justify-center rounded-2xl border-2 border-ink bg-lime px-8 font-display text-lg font-bold tracking-tight text-ink shadow-hardSm disabled:opacity-50"
      >
        {plan.isSubmitting ? "Saving your plan…" : "Get my fixed price →"}
      </button>
    </div>
  );
}

function SubmittedPlan({ plan }: { plan: ReturnType<typeof useCustomerPlan> }) {
  const lead = plan.submittedLead;
  if (!lead) return null;
  const upfront = plan.form.paymentType === "upfront";
  return (
    <div className="flex flex-col gap-6">
      <h2 className="m-0 font-powlo-display text-4xl font-bold tracking-tight">Your proposal is ready.</h2>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          className="rounded-2xl border-2 border-white/70 px-5 py-3 font-bold"
          onClick={() => {
            const anchor = document.createElement("a");
            anchor.href = lead.proposalPdfUrl ?? `${API_BASE}/api/quotes/${lead.id}/proposal-pdf`;
            anchor.target = "_blank";
            anchor.rel = "noreferrer";
            anchor.click();
          }}
        >
          Download PDF
        </button>
        <Link href="/portal" className="rounded-2xl bg-white px-5 py-3 font-bold text-powlo-ink no-underline">
          Open portal
        </Link>
      </div>
      {upfront ? (
        <div className="flex flex-wrap gap-3">
          <button type="button" className="rounded-2xl bg-powlo-lime px-5 py-3 font-bold text-powlo-ink" disabled={plan.contractLoading} onClick={() => void plan.handleContractReady()}>
            {lead.contractSignedAt ? "Agreement signed" : "Sign agreement"}
          </button>
          {lead.contractSignedAt && (
            <button type="button" className="rounded-2xl border-2 border-white px-5 py-3 font-bold" disabled={plan.depositPending} onClick={plan.handlePayDeposit}>
              Pay deposit
            </button>
          )}
        </div>
      ) : (
        <button type="button" className="w-fit rounded-2xl bg-powlo-lime px-5 py-3 font-bold text-powlo-ink" onClick={() => void plan.handleApplyForFinance()}>
          Apply for finance
        </button>
      )}
      <div className="overflow-hidden rounded-2xl bg-white text-powlo-ink">
        {plan.proposalHtmlLoading && <p className="p-6 text-sm text-powlo-muted">Loading proposal…</p>}
        {plan.proposalHtmlError && <p className="p-6 text-sm text-red-700">The proposal could not be loaded.</p>}
        {plan.proposalHtml && <ProposalHtmlPreview html={plan.proposalHtml} />}
      </div>
      <ContractSigningModal
        signingUrl={plan.contractSigningUrl}
        open={plan.contractModalOpen}
        onClose={() => {
          plan.setContractModalOpen(false);
          plan.setContractSigningUrl(null);
        }}
        onCompleted={() => void plan.handleContractCompleted()}
      />
      <CustomerAvailabilityModal
        quoteId={lead.id}
        open={plan.availabilityOpen}
        onOpenChange={plan.setAvailabilityOpen}
        onSaved={plan.rememberAvailability}
      />
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <div>
      <Label className="mb-1 block text-sm text-white">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 rounded-2xl border-2 border-white bg-white px-4 text-base text-[#0C0D10] shadow-none placeholder:text-[#6B6E76] dark:border-white dark:bg-white dark:text-[#0C0D10]"
      />
    </div>
  );
}
